import React, { Suspense, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ToastContainer } from "react-toastify";
import { createUserInfoSlice } from "./app/reducer/globalSlice";
import { usePresence } from "./hooks/usePresence";
import packageJson from "../package.json";
import CacheBuster from "react-cache-buster"; // 👈 Import CacheBuster
import "react-toastify/dist/ReactToastify.css";
import "bootstrap/dist/css/bootstrap.min.css";
import "./globalstyles/css/style.css";

// Global styles
import "@fortawesome/fontawesome-free/css/all.min.css";
import "./globalstyles/css/bootstrap.css";
import "./globalstyles/css/bootstrap.min.css";
import "./globalstyles/css/style.css";
import "./globalstyles/css/presence.css";

import GlobalLoader from "./views/components/GlobalLoader/GlobalLoader";
import BackgroundChatIframe from "./views/components/BackGroundIframe";
import { applyUnreadDelta, setGlobalUnread } from "./app/globalCountSlice";
import { addNotification, deleteNotification, setNotificationCount, updateNotification } from "./app/notificationSlice";
import { incrementAnnouncementCount } from "./app/announcementCountSlice";
import { useLazyGetOnboardingConfigsQuery } from "./app/modules/globalSlice";
import { setOnboardingCache } from "./utils/onboardingCache";
import ScrollGuard from "./views/components/ScrollGuard";
import ApprovalNotice from "./views/components/ApprovalNotice/ApprovalNotice";
import EventCancelledNotice from "./views/components/EventCancelledNotice/EventCancelledNotice";

// Lazy load components to enable code splitting
const Approutes = React.lazy(() => import("./routes"));

const App = () => {
  const userInfo = useSelector((state) => state.userInfo);
  const userInfoSlice = createUserInfoSlice();
  const { setUserInfo, resetUserInfo } = userInfoSlice.actions;

  const [isLoggedIn, setIsLoggedIn] = useState(true);
  const dispatch = useDispatch();

  // Onboarding tour state sync — called once when user is authenticated
  const [getOnboardingConfigs] = useLazyGetOnboardingConfigsQuery();

  // localStorage key map: screen → tourKey
  const TOUR_KEY_MAP = {
    dashboard:             'hasSeenDashboardTour',
    events:                'hasSeenEventsTour',
    profile:               'hasSeenProfileTour',
    event_creator_invite:  'hasSeenCreatorSidebarTour',
    event_creator_manage:  'hasSeenMyEventsSidebarTour',
  };

  const version = packageJson.version;
  console.log("version is: ", version);

  // Handle user presence and session
  let userData = null;
  try {
    if (userInfo) {
      userData = typeof userInfo === "string" ? JSON.parse(userInfo) : userInfo;
    }
  } catch (error) {
    console.error("Error parsing userInfo:", error);
    try {
      const localUserData = localStorage.getItem("user");
      if (localUserData) userData = JSON.parse(localUserData);
    } catch (localError) {
      console.error("Error parsing localStorage user data:", localError);
    }
  }

  const userId =
    userData?.id || userData?.userId || userData?._id || userData?.user_id;

  useEffect(() => {
    if (userInfo || localStorage.getItem("authToken")) {
      setIsLoggedIn(true);
      let userData = JSON.parse(localStorage.getItem("user"));
      if (!userInfo && userData) {
        dispatch(setUserInfo(JSON.stringify(userData)));
      }

      // Fetch onboarding config once and pre-populate all tour localStorage keys
      getOnboardingConfigs()
        .unwrap()
        .then((res) => {
          const screens = res?.data || [];

          // Cache the full data so pages can merge API titles/messages
          setOnboardingCache(screens);

          // Pre-set localStorage for completed screens so tour never shows
          screens.forEach((screen) => {
            if (screen.isCompleted) {
              const tourKey = TOUR_KEY_MAP[screen.screen];
              if (tourKey) {
                localStorage.setItem(tourKey, 'true');
              }
            }
          });
        })
        .catch(() => {});
    } else {
      dispatch(resetUserInfo({}));
      setIsLoggedIn(false);
    }
  }, [userInfo, dispatch]);

  //   useEffect(() => {
  //   const handleMessage = (event) => {
  //     if (event.origin !== "https://chatui-ashy-gamma.vercel.app") return;

  //     if (event.data?.type === "CHAT_READY") {
  //       const token =
  //         localStorage.getItem("authToken") ||
  //         sessionStorage.getItem("authToken");

  //       // respond to THE iframe that sent CHAT_READY
  //       event.source?.postMessage(
  //         {
  //           type: "OPEN_CHAT",
  //           payload: { token },
  //         },
  //         event.origin
  //       );
  //     }
  //   };

  //   window.addEventListener("message", handleMessage);
  //   return () => window.removeEventListener("message", handleMessage);
  // }, []);

  // const isProduction = process.env.NODE_ENV === "production";

  useEffect(() => {
    const handleMessage = (event) => {
      if (event.origin !== "https://chat-client-v1.vercel.app") return;

      const { type, data } = event.data || {};
      console.log(event.data, "recieved message from iframe");

      switch (type) {
        case "CHAT_READY": {
          const token =
            localStorage.getItem("authToken") ||
            sessionStorage.getItem("authToken");
          event.source?.postMessage(
            { type: "OPEN_CHAT", payload: { token } },
            event.origin,
          );
          break;
        }
        case "globalUnreadCount": {
          const delta = data?.delta ?? 0;
          console.log("purple", data);

          dispatch(applyUnreadDelta(delta));

          break;
        }

        case "CHAT_BACK": {              // 👈 ADD THIS
          window.history.back();
          break;
        }

      

        case "NAVIGATE_PROFILE": {
          const userId = data?.userId;
          if (!userId) return;

          // ✅ React Router (most likely in your app)
          window.history.pushState({}, "", `/user-profile/${userId}`);
          window.dispatchEvent(new PopStateEvent("popstate"));

          break;
        }

        case "NAVIGATE_TO_MARKETPLACE": {
          const listingId = data?.listingId;
          if (!listingId) return;

          // ✅ Route in parent app
          window.history.pushState({}, "", `/marketplace-details/${listingId}`);
          window.dispatchEvent(new PopStateEvent("popstate"));
          break;
        }


        case "NEW_NOTIFICATION": {
          dispatch(addNotification(data));
          if (data?.type === "ANNOUNCEMENT") {
            dispatch(incrementAnnouncementCount());
          }
          break;
        }

        case "NOTIFICATION_UPDATE": {
          dispatch(updateNotification(data));
          break;
        }

        case "NOTIFICATION_DELETE": {
          dispatch(deleteNotification(data.notificationId)); // ✅ camelCase confirmed
          break;
        }
        case "NOTIFICATION_COUNT": {
          dispatch(setNotificationCount(event.data.count)); // ✅ already correct
          break;
        }

        default:
          break;
      }
    };

    window.addEventListener("message", handleMessage);
    return () => window.removeEventListener("message", handleMessage);
  }, [dispatch]);




  

  return (
    <CacheBuster
      currentVersion={version}
      // isEnabled={isProduction}
      isVerboseMode={false}
      loadingComponent={<GlobalLoader />} // 👈 Shown while checking for updates
      metaFileDirectory={"."}
    >
      <Suspense
        fallback={
          <div>
            <GlobalLoader />
          </div>
        }
      >
        <Approutes isLoggedIn={isLoggedIn} />
      </Suspense>

      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
      <ScrollGuard />
      <ApprovalNotice />
      <EventCancelledNotice />
      {/* <ScrollToTop /> */}
    </CacheBuster>
  );
};

export default App;
